import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Nav, Badge, Dropdown, Alert, Spinner, Form, Table, Button } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { usePaginatedList } from '../hooks/usePaginatedList';
import PaginationControls from '../components/commons/PaginationControls';
import { ExcelItems } from '../components/Items/ExcelItems';
import { EditarItem } from '../components/Items/EditarItem';
import ItemDescripcionModal from '../components/Items/ItemDescripcionModal';
import { PatronesItem } from '../components/Items/PatronesItem';
import {
    getItemsTodos,
    getItemsContable,
    inactivarItem,
    activarItem,
    migrarItemsEstado,
    getDataList,
} from '../api/common';

export const Items = () => {
    const [tabActivo, setTabActivo] = useState('activos');
    const [todosItems, setTodosItems] = useState([]);


    const [loadingTodos, setLoadingTodos] = useState(false);
    const [migrando, setMigrando] = useState(false);
    const [hayProveedor, setHayProveedor] = useState(false);
    const [magnitudes, setMagnitudes] = useState([]);
    const [modalEditar, setModalEditar] = useState({ show: false, item: null });
    const [modalDescripcion, setModalDescripcion] = useState({ show: false, item: null });
    const [modalPatrones, setModalPatrones] = useState({ show: false, item: null });
    const [inactivandoId, setInactivandoId] = useState(null);
    const [activandoId, setActivandoId] = useState(null);

    // El filtro de búsqueda se aplica en el backend (vía POST /Items/paginar
     // con `conditions`). Buscamos en `nombre`, `alias` y `codigo` para que
     // encontrar un item no dependa de la página en la que esté.
    const buildBusquedaOr = (term) => {
        const t = (term || '').trim();
        if (!t) return null;
        const re = { $regex: t, $options: 'i' };
        const or = [{ nombre: re }, { alias: re }];
        // Si lo escrito es numérico, también buscar por `codigo` (Number).
        if (/^\d+$/.test(t)) or.push({ codigo: parseInt(t, 10) });
        return or;
    };

    const buildConditionsActivos = useCallback((filters) => {
        const cond = {
            $or: [{ estado: 'Activo' }, { estado: { $exists: false } }],
        };
        const busqueda = buildBusquedaOr(filters.nombre);
        if (busqueda) {
            // Mongo trata $or top-level y $and top-level con AND implícito,
            // por lo que podemos combinar sin perder el filtro de estado.
            cond.$and = [{ $or: busqueda }];
        }
        if (filters.magnitud) cond.magnitud = filters.magnitud;
        return cond;
    }, []);

    const buildConditionsInactivos = useCallback((filters) => {
        const cond = { estado: 'Inactivo' };
        const busqueda = buildBusquedaOr(filters.nombre);
        if (busqueda) cond.$or = busqueda;
        if (filters.magnitud) cond.magnitud = filters.magnitud;
        return cond;
    }, []);

    const paginacionActivos = usePaginatedList({
        endpoint: '/Items/paginar',
        initialFilters: { nombre: '', magnitud: '', sort: 'asc' },
        buildConditions: buildConditionsActivos,
        initialPerPage: 20,
    });

    console.log('paginacion activos:', paginacionActivos);

    const paginacionInactivos = usePaginatedList({
        endpoint: '/Items/paginar',
        initialFilters: { nombre: '', magnitud: '', sort: 'asc' },
        buildConditions: buildConditionsInactivos,
        initialPerPage: 20,
    });



    useEffect(() => {
        getItemsContable(0, 1)
            .then(res => setHayProveedor(res?.data?.data?.hayProveedor === true))
            .catch(() => setHayProveedor(false));
    }, []);

    useEffect(() => {
        getDataList('magnitud')
            .then(res => setMagnitudes(res?.data?.data || []))
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (tabActivo === 'todos') {
            setLoadingTodos(true);
            getItemsTodos()
                .then(res => setTodosItems(res?.data?.data || []))
                .catch(() => setTodosItems([]))
                .finally(() => setLoadingTodos(false));
        }
    }, [tabActivo]);

    const handleTabChange = (tab) => {
        setTabActivo(tab);
        if (tab === 'activos') paginacionActivos.resetFilters();
        if (tab === 'inactivos') paginacionInactivos.resetFilters();
    };

    const handleInactivar = async (item) => {
        const result = await Swal.fire({
            title: '¿Inactivar ítem?',
            text: `"${item.nombre}" no aparecerá en nuevas órdenes.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, inactivar',
            cancelButtonText: 'Cancelar',
        });
        if (!result.isConfirmed) return;
        setInactivandoId(item._id);
        try {
            await inactivarItem(item._id);
            paginacionActivos.refresh();
            paginacionInactivos.refresh();
        } catch (err) {
            const detail = err.response?.data?.detail || err.response?.data?.error?.message || err.message;
            const canForce = err.response?.data?.canForce;
            if (canForce) {
                const forceResult = await Swal.fire({
                    title: 'Ítem en uso',
                    text: `${detail}. ¿Desea inactivarlo de todas formas?`,
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Sí, inactivar de todos modos',
                    cancelButtonText: 'Cancelar',
                    confirmButtonColor: '#d33',
                });
                if (forceResult.isConfirmed) {
                    try {
                        await inactivarItem(item._id, true);
                        paginacionActivos.refresh();
                        paginacionInactivos.refresh();
                    } catch (err2) {
                        const msg2 = err2.response?.data?.detail || err2.message;
                        Swal.fire('Error', msg2, 'error');
                    }
                }
            } else {
                Swal.fire('No se puede inactivar', detail, 'error');
            }
        } finally {
            setInactivandoId(null);
        }
    };

    const handleActivar = async (item) => {
        const result = await Swal.fire({
            title: '¿Activar ítem?',
            text: `"${item.nombre}" volverá a estar disponible.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, activar',
            cancelButtonText: 'Cancelar',
        });
        if (!result.isConfirmed) return;
        setActivandoId(item._id);
        try {
            await activarItem(item._id);
            paginacionActivos.refresh();
            paginacionInactivos.refresh();
        } catch (err) {
            const msg = err.response?.data?.detail || err.message;
            Swal.fire('Error', msg, 'error');
        } finally {
            setActivandoId(null);
        }
    };

    const renderFiltros = (paginacion) => (
        <Row className="mb-3 g-2">
            <Col md={4} sm={12}>
                <Form.Control
                    type="text"
                    placeholder="Buscar por nombre, alias o código..."
                    value={paginacion.filters.nombre || ''}
                    onChange={(e) => paginacion.updateFilter('nombre', e.target.value)}
                />
            </Col>
            <Col md={3} sm={6}>
                <Form.Select
                    value={paginacion.filters.magnitud || ''}
                    onChange={(e) => paginacion.updateFilter('magnitud', e.target.value)}
                >
                    <option value="">Todas las magnitudes</option>
                    {magnitudes.map(m => (
                        <option key={m._id} value={m._id}>{m.nombre}</option>
                    ))}
                </Form.Select>
            </Col>
            <Col md={2} sm={6}>
                <Form.Select
                    value={paginacion.filters.sort || 'asc'}
                    onChange={(e) => paginacion.updateFilter('sort', e.target.value)}
                >
                    <option value="asc">A → Z</option>
                    <option value="desc">Z → A</option>
                </Form.Select>
            </Col>
        </Row>
    );

    const renderTabla = (items, esTab) => (
        <Table striped hover responsive>
            <thead className="table-info">
                <tr>
                    <th>Código</th>
                    <th>Nombre</th>
                    <th>Magnitud</th>
                    <th>Consecutivo</th>
                    <th>Precio</th>
                    <th>Contable</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                </tr>
            </thead>
            <tbody>
                {items.length === 0 ? (
                    <tr>
                        <td colSpan={8} className="text-center text-muted py-3">
                            Sin ítems encontrados
                        </td>
                    </tr>
                ) : items.map(it => (
                    <tr key={it._id}>
                        <td>{it.codigo}</td>
                        <td title={it.alias && it.alias !== it.nombre ? `Alias: ${it.alias}` : undefined}>{it.nombre}</td>
                        <td>{it.magnitud?.nombre || '—'}</td>
                        <td>{it.magnitud?.consecutivo ?? '—'}</td>
                        <td>${it.precio ?? 0}</td>
                        <td>
                            {it.contabilidad?.id
                                ? <Badge bg="success" title={it.contabilidad.name}>✅</Badge>
                                : '—'}
                        </td>
                        <td>
                            <Badge
                                bg={it.estado === 'Activo' ? 'success' : 'warning'}
                                text={it.estado === 'Inactivo' ? 'dark' : undefined}
                            >
                                {it.estado || 'Activo'}
                            </Badge>
                        </td>
                        <td>
                            <Dropdown>
                                <Dropdown.Toggle variant="outline-secondary" size="sm" id={`dd-${it._id}`}>
                                    ⋮
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item onClick={() => setModalEditar({ show: true, item: it })}>
                                        ✏️ Editar
                                    </Dropdown.Item>
                                    {esTab === 'activos' && (
                                        <Dropdown.Item onClick={() => setModalEditar({ show: true, item: it })}>
                                            🔗 {it.contabilidad?.id ? 'Cambiar vinculación' : 'Vincular contable'}
                                        </Dropdown.Item>
                                    )}
                                    <Dropdown.Item onClick={() => setModalDescripcion({ show: true, item: it })}>
                                        📋 Ver Descripción
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => setModalPatrones({ show: true, item: it })}>
                                        🔬 Ver Patrones
                                    </Dropdown.Item>
                                    <Dropdown.Divider />
                                    {esTab === 'activos' && (
                                        <Dropdown.Item
                                            className="text-danger"
                                            onClick={() => handleInactivar(it)}
                                            disabled={inactivandoId === it._id}
                                        >
                                            {inactivandoId === it._id ? 'Inactivando...' : '🚫 Inactivar'}
                                        </Dropdown.Item>
                                    )}
                                    {esTab === 'inactivos' && (
                                        <Dropdown.Item
                                            className="text-success"
                                            onClick={() => handleActivar(it)}
                                            disabled={activandoId === it._id}
                                        >
                                            {activandoId === it._id ? 'Activando...' : '✅ Activar'}
                                        </Dropdown.Item>
                                    )}
                                </Dropdown.Menu>
                            </Dropdown>
                        </td>
                    </tr>
                ))}
            </tbody>
        </Table>
    );

    return (
        <Container fluid>
            <Row className="align-items-center mb-3">
                <Col>
                    <h4 className="mb-0">Items / Equipos</h4>
                </Col>
                <Col xs="auto" className="d-flex gap-2">
                    <button
                        className="btn btn-primary"
                        onClick={() => setModalEditar({ show: true, item: null })}
                    >
                        + Agregar Ítem
                    </button>
                    <button
                        className="btn btn-outline-success"
                        onClick={() => ExcelItems(todosItems.length > 0 ? todosItems : paginacionActivos.results)}
                    >
                        Exportar Excel
                    </button>
                </Col>
            </Row>

            <Nav variant="tabs" className="mb-3">
                <Nav.Item>
                    <Nav.Link active={tabActivo === 'activos'} onClick={() => handleTabChange('activos')}>
                        Activos
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link active={tabActivo === 'inactivos'} onClick={() => handleTabChange('inactivos')}>
                        Inactivos
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link active={tabActivo === 'todos'} onClick={() => handleTabChange('todos')}>
                        Todos
                    </Nav.Link>
                </Nav.Item>
            </Nav>

            {tabActivo === 'activos' && (
                <>
                    {renderFiltros(paginacionActivos)}
                    {paginacionActivos.error && (
                        <Alert variant="danger">{paginacionActivos.error}</Alert>
                    )}
                    {paginacionActivos.loading ? (
                        <div className="text-center py-4"><Spinner animation="border" /></div>
                    ) : renderTabla(paginacionActivos.results, 'activos')}
                    <PaginationControls
                        {...paginacionActivos.pagination}
                        onPageChange={paginacionActivos.changePage}
                        onPerPageChange={paginacionActivos.changePerPage}
                        onRefresh={paginacionActivos.refresh}
                        loading={paginacionActivos.loading}
                    />
                </>
            )}

            {tabActivo === 'inactivos' && (
                <>
                    {renderFiltros(paginacionInactivos)}
                    {paginacionInactivos.loading ? (
                        <div className="text-center py-4"><Spinner animation="border" /></div>
                    ) : renderTabla(paginacionInactivos.results, 'inactivos')}
                    <PaginationControls
                        {...paginacionInactivos.pagination}
                        onPageChange={paginacionInactivos.changePage}
                        onPerPageChange={paginacionInactivos.changePerPage}
                        onRefresh={paginacionInactivos.refresh}
                        loading={paginacionInactivos.loading}
                    />
                </>
            )}

            {tabActivo === 'todos' && (
                <>
                    <Row className="mb-3">
                        <Col xs="auto">
                            <Button
                                variant="outline-warning"
                                size="sm"
                                disabled={migrando}
                                onClick={async () => {
                                    setMigrando(true);
                                    try {
                                        const res = await migrarItemsEstado();
                                        const modified = res?.data?.data?.modified ?? 0;
                                        Swal.fire('Migración completada', `${modified} ítems actualizados con estado 'Activo'`, 'success');
                                        setLoadingTodos(true);
                                        getItemsTodos()
                                            .then(r => setTodosItems(r?.data?.data || []))
                                            .catch(() => setTodosItems([]))
                                            .finally(() => setLoadingTodos(false));
                                        paginacionActivos.refresh();
                                    } catch (err) {
                                        const msg = err.response?.data?.detail || err.message;
                                        Swal.fire('Error', msg, 'error');
                                    } finally {
                                        setMigrando(false);
                                    }
                                }}
                            >
                                {migrando ? <><Spinner animation="border" size="sm" className="me-1" />Migrando...</> : 'Actualizar campo Estado'}
                            </Button>
                        </Col>
                    </Row>
                    {loadingTodos ? (
                        <div className="text-center py-4"><Spinner animation="border" /></div>
                    ) : renderTabla(todosItems, 'todos')}
                </>
            )}

            <EditarItem
                show={modalEditar.show}
                onHide={() => setModalEditar({ show: false, item: null })}
                item={modalEditar.item}
                hayProveedor={hayProveedor}
                onSaved={() => {
                    paginacionActivos.refresh();
                    paginacionInactivos.refresh();
                }}
            />
            <ItemDescripcionModal
                show={modalDescripcion.show}
                onHide={() => setModalDescripcion({ show: false, item: null })}
                item={modalDescripcion.item}
            />
            <PatronesItem
                show={modalPatrones.show}
                onHide={() => setModalPatrones({ show: false, item: null })}
                item={modalPatrones.item}
            />
        </Container>
    );
};

